<!--
 NACHA Export Validation Issues Modal - Enhanced with bank account fixing capability

 2025-09-18: Enhanced modal with fix functionality for bank account validation issues
 Features: Direct bank account fixing from validation errors, real-time validation updates
-->

<template>
  <v-card>
    <v-card-title class="text-h5 align-center bb-1">
      <v-icon class="pr-2" color="warning">mdi-alert</v-icon>
      NACHA Export Validation Issues
      <v-spacer />
      <v-btn icon @click="onCancel">
        <v-icon color="primary">mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="pt-4">
      <v-alert type="warning" outlined>
        <div class="text-body-1">
          <strong
            >{{ validationErrors.length }} of
            {{ totalListings }} listings</strong
          >
          have validation issues and cannot be included in the NACHA export.
        </div>
      </v-alert>

      <div v-if="validListings > 0" class="text-body-1 mt-4 mb-4">
        <strong>{{ validListings }} listings</strong> passed validation and can
        be exported.
      </div>

      <v-expansion-panels v-if="validationErrors.length <= 10" class="mt-4">
        <v-expansion-panel
          v-for="(item, index) in validationErrors"
          :key="index"
        >
          <v-expansion-panel-header>
            <div>
              <strong>{{
                item.listing.nickname || `Listing ${item.listing.id}`
              }}</strong>
              <span class="text-caption ml-2"
                >({{ item.errors.length }} issues)</span
              >
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list dense>
              <v-list-item
                v-for="(error, errorIndex) in item.errors"
                :key="errorIndex"
              >
                <v-list-item-icon>
                  <v-icon small color="error">mdi-alert-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ error }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-card-actions class="pt-2">
              <v-spacer />
              <v-btn
                v-if="hasBankAccountIssues(item.errors)"
                small
                color="primary"
                outlined
                @click="fixBankAccounts(item.listing, item.errors)"
              >
                <v-icon small left>mdi-bank</v-icon>
                Fix Bank Accounts
              </v-btn>
            </v-card-actions>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div v-else class="mt-4">
        <v-simple-table dense>
          <template #default>
            <thead>
              <tr>
                <th>Listing</th>
                <th>Issues</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in validationErrors.slice(0, 10)"
                :key="index"
              >
                <td>
                  {{ item.listing.nickname || `Listing ${item.listing.id}` }}
                </td>
                <td>
                  <ul class="pl-4 my-1">
                    <li
                      v-for="(error, errorIndex) in item.errors.slice(0, 3)"
                      :key="errorIndex"
                      class="text-caption"
                    >
                      {{ error }}
                    </li>
                    <li
                      v-if="item.errors.length > 3"
                      class="text-caption text--secondary"
                    >
                      ... and {{ item.errors.length - 3 }} more issues
                    </li>
                  </ul>
                </td>
              </tr>
              <tr v-if="validationErrors.length > 10">
                <td colspan="2" class="text-center">
                  <em
                    >... and {{ validationErrors.length - 10 }} more listings
                    with issues</em
                  >
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-card-text>

    <v-divider />

    <v-card-actions class="justify-space-around pa-4">
      <v-btn depressed @click="onCancel">Cancel Export</v-btn>
      <v-spacer />
      <v-btn v-if="validListings > 0" color="primary" @click="onProceed">
        Export {{ validListings }} Valid Listings
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Toaster from '@/utils/toaster'

export default {
  name: 'NachaValidationErrorsModal',
  props: {
    validationErrors: {
      type: Array,
      required: true,
    },
    totalListings: {
      type: Number,
      required: true,
    },
    validListings: {
      type: Number,
      required: true,
    },
    onProceed: {
      type: Function,
      required: true,
    },
    onCancel: {
      type: Function,
      required: true,
    },
    onListingFixed: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    hasBankAccountIssues(errors) {
      return errors.some(
        error =>
          error.toLowerCase().includes('bank account') ||
          error.toLowerCase().includes('routing number') ||
          error.toLowerCase().includes('account number') ||
          error.toLowerCase().includes('account name')
      )
    },
    fixBankAccounts(listing, errors) {
      const bankAccountErrors = errors.filter(
        error =>
          error.toLowerCase().includes('bank account') ||
          error.toLowerCase().includes('routing number') ||
          error.toLowerCase().includes('account number') ||
          error.toLowerCase().includes('account name')
      )

      this.$store.commit('showModal', {
        name: 'BankAccountFixModal',
        maxWidth: 800,
        isPersistent: true,
        props: {
          listing: listing,
          initialErrors: bankAccountErrors,
          onSave: updatedListing => {
            // Update the listing in the validation errors
            const errorItem = this.validationErrors.find(
              item => item.listing.id === listing.id
            )
            if (errorItem) {
              errorItem.listing = updatedListing
              // Re-validate the listing to update errors
              this.revalidateListing(errorItem)
            }

            // Call parent callback if provided to refresh the main table
            if (this.onListingFixed) {
              this.onListingFixed(updatedListing)
            }

            // Force update of the validation errors display
            this.$forceUpdate()

            // If all errors are fixed, update the valid listings count
            this.updateValidListingsCount()

            this.$store.commit('hideModal')
          },
          onClose: () => {
            this.$store.commit('hideModal')
          },
        },
      })
    },
    revalidateListing(errorItem) {
      // Re-run validation logic on the updated listing
      const errors = this.validateListingForNacha(errorItem.listing)
      errorItem.errors = errors

      // Force reactivity update
      this.$forceUpdate()
    },
    validateListingForNacha(listing) {
      const errors = []

      // Validate listing bank account
      if (!listing.bank_account) {
        errors.push('Missing listing bank account info')
      } else if (!listing.bank_account.account_number) {
        errors.push('Missing listing bank account number')
      }

      // Validate owner bank account
      if (!listing.owner_bank_account) {
        errors.push('Missing listing owner bank account info')
      } else {
        if (!listing.owner_bank_account.account_name) {
          errors.push('Missing listing owner bank account name')
        }
        if (!listing.owner_bank_account.account_number) {
          errors.push('Missing listing owner bank account number')
        }
        if (!listing.owner_bank_account.routing_number) {
          errors.push('Missing listing owner bank account routing number')
        } else if (
          !listing.owner_bank_account.routing_number.match(/^\d{9}$/)
        ) {
          errors.push('Invalid routing number (must be 9 digits)')
        }
      }

      // Validate referrer bank account if applicable
      if (listing.referrer_percent && listing.referrer_percent > 0) {
        if (!listing.referrer_bank_account) {
          errors.push('Missing listing referrer bank account info')
        } else {
          if (!listing.referrer_bank_account.account_name) {
            errors.push('Missing listing referrer bank account name')
          }
          if (!listing.referrer_bank_account.account_number) {
            errors.push('Missing listing referrer bank account number')
          }
          if (!listing.referrer_bank_account.routing_number) {
            errors.push('Missing listing referrer bank account routing number')
          } else if (
            !listing.referrer_bank_account.routing_number.match(/^\d{9}$/)
          ) {
            errors.push('Referrer: Invalid routing number (must be 9 digits)')
          }
        }
      }

      return errors
    },
    updateValidListingsCount() {
      // Count listings without errors
      const validCount = this.validationErrors.filter(
        item => item.errors.length === 0
      ).length
      const totalListings = this.validationErrors.length

      // Update the props (this will trigger reactivity)
      this.$emit('validation-updated', {
        validListings: validCount,
        totalListings: totalListings,
        hasAllValid: validCount === totalListings,
      })

      // If all errors are resolved, show success message
      if (validCount === totalListings && totalListings > 0) {
        Toaster.show([
          {
            type: 'success',
            text: 'All validation issues have been resolved! You can now proceed with the export.',
          },
        ])
      }
    },
  },
}
</script>

<style scoped></style>
